import { get as _get } from 'lodash';
import { v4 as uuid } from 'uuid';
import { MULTIPLE_VEHICLES_TEXT } from '../Constants/Constants';
import { GenericProductType, PolicyStatus } from '../types/type';
import { determineConversePartner, getClaimType } from './Utilities';

/**
 * Checks if a policy term status indicates the policy is in force.
 * Handles both 'InForce' and 'In Force' variants (case-insensitive).
 *
 * @param policyTermStatus - The policy term status string to check
 * @returns true if the policy is in force, false otherwise
 */
export function isPolicyInForce(policyTermStatus: string): boolean {
  if (!policyTermStatus) {
    return false;
  }
  const normalizedStatus = policyTermStatus.toUpperCase();
  return normalizedStatus === 'INFORCE' || normalizedStatus === 'IN FORCE' || normalizedStatus === 'IN_FORCE';
}

export function isAutoClaim(policyType: string): boolean {
  switch (policyType.toLowerCase()) {
    case 'personalauto':
    case 'boat_afi':
    case 'cycle_afi':
    case 'nonowners_afi':
    case 'misc_afi':
    case 'maacbasgnrisk_afi':
    case 'assignedrisk_afi':
    case 'motorhome':
    case 'nonownerveh':
    case 'miscvehpolicy':
    case 'miscveh':
    case 'famcar':
    case 'boat':
    case 'motorcyclepolicy':
      return true;
    default:
      return false;
  }
}

export function mapPolicyType(policyType: string): GenericProductType {
  if (!policyType) {
    return GenericProductType.Other;
  }
  switch (policyType.toLowerCase()) {
    case 'personalauto':
    case 'boat_afi':
    case 'cycle_afi':
    case 'nonowners_afi':
    case 'misc_afi':
    case 'maacbasgnrisk_afi':
    case 'assignedrisk_afi':
    case 'motorhome':
    case 'nonownerveh':
    case 'miscvehpolicy':
    case 'miscveh':
    case 'famcar':
    case 'boat':
    case 'motorcyclepolicy':
    case 'auto':
      return GenericProductType.Auto;
    case 'businessauto':
    case 'commercialauto':
      return GenericProductType.CommercialAuto;
    case 'homeowners_afi':
    case 'manufactured_homeowners':
    case 'personalproperty':
    case 'homeowners':
    case 'homeownersline_hoe':
    case 'homeownersform1_afi':
    case 'homeownersform2_afi':
    case 'homeownersform3_afi':
    case 'homeownersform3_ext':
    case 'homeownersform4_afi':
    case 'homeownersform6_afi':
    case 'homeownersform6_ext':
    case 'homeownersformgs_afi':
    case 'homeownersformgs_ext':
    case 'homeownersgs_ext':
    case 'homeownersgs_afi':
    case 'renters_afi':
    case 'renters':
    case 'customvalueform1_afi':
    case 'customvalueform3_afi':
    case 'dwellingbasic_afi':
    case 'dwellingbroad_afi':
    case 'dwellingspecial_ext':
    case 'mobhomeseasonal_afi':
    case 'mobilehomeform1_afi':
    case 'mobilehomeform3_afi':
    case 'mobilehomegs_afi':
    case 'condo_afi':
    case 'condominium_unit-owners':
    case 'manufacturedhome_afi':
    case 'secondhome_afi':
    case 'tenantliability_ext':
    case 'dp1':
    case 'dp2':
    case 'dpm':
    case 'mh3':
    case 'ho4':
    case 'ho3':
    case 'ho5':
    case 'ho6':
    case 'formho1':
    case 'formho2':
    case 'formho3':
    case 'formho6':
    case 'formho4':
    case 'formhf9':
    case 'home':
      return GenericProductType.Home;
    case 'bophab_afi':
    case 'bkp_afi':
    case 'businessowners':
    case 'businessownerspolicy':
    case 'businesskeypolicy':
    case 'cpp_afi':
    case 'contractorspackageprogram':
    case 'commercialpackage':
    case 'vltbopp_afi':
    case 'commercialproperty':
    case 'commercialpackagepolicy':
      return GenericProductType.CommercialProperty;
    case 'farmranch_afi':
    case 'farmranch':
    case 'farmowners':
      return GenericProductType.FarmRanch;
    case 'generalliability':
    case 'frumbrella':
    case 'fidelity_ext':
    case 'commercialumbrella':
    case 'eco_ext':
    case 'suretycontract_ext':
    case 'suretyother_ext':
    case 'nonprofitdirectionsandofficers':
    case 'd_and_o':
    case 'farmranchumb_afi':
      return GenericProductType.GeneralLiability;
    case 'workerscomp':
    case 'workerscompensation':
    case 'workers_comp':
    case 'workers_compensation':
      return GenericProductType.WorkersComp;
    default:
      return GenericProductType.Other;
  }
}

function mapPolicyStatus(status: string) {
  if (!status) {
    return PolicyStatus.Other;
  }
  switch (status.toLowerCase()) {
    case 'inforce':
    case 'in force':
      return PolicyStatus.Inforce;
    case 'cancelled':
    case 'canceled':
      return PolicyStatus.Cancelled;
    case 'inactive':
      return PolicyStatus.Inactive;
    case 'scheduled':
      return PolicyStatus.Scheduled;
    default:
      return PolicyStatus.Other;
  }
}

export function mapDetailedPolicies(
  detailedPoliciesResponse: any[],
  opco: string,
  partnerId: string,
  roleType: string
) {
  return detailedPoliciesResponse.map((detailedPolicy: any) => {
    const generalizedProductType = mapPolicyType(detailedPolicy.generalizedProductType);

    return {
      ...detailedPolicy,
      policyAddress: Array.isArray(detailedPolicy.policyAddress)
        ? detailedPolicy.policyAddress[0]
        : detailedPolicy.policyAddress,
      risks: updateRisks(detailedPolicy, generalizedProductType),
      productType: detailedPolicy.generalizedProductType,
      generalizedProductType: generalizedProductType,
      policyOpCo: determineConversePartner(opco, partnerId, roleType, _get(detailedPolicy, 'partnerCode')),
    };
  });
}

export function mapE1PPolicies(policyResponse: any, opco: string, partnerId: string, roleType: string) {
  const productType = _get(policyResponse, 'productType', '');
  const generalizedProductType = mapPolicyType(productType);

  console.log('🔍 mapE1PPolicies - productType:', productType);
  console.log('🔍 mapE1PPolicies - generalizedProductType:', generalizedProductType);

  const policy: any = {
    generalizedProductType: generalizedProductType,
    periodStartDate: _get(policyResponse, 'periodStartDate', ''),
    policyNumber: _get(policyResponse, 'policyNumber', ''),
    address: _get(policyResponse, 'policyAddress', ''),
    policyOpCo: determineConversePartner(opco, partnerId, roleType, _get(policyResponse, 'partnerId')),
    policyRoles: generatePolicyRoles(policyResponse),
    contacts: _get(policyResponse, 'contacts', []),
    policy: {
      productType: productType,
      policyTermStatus: _get(policyResponse, 'policyTermStatus', ''),
      policyStatus: isPolicyInForce(_get(policyResponse, 'policyTermStatus', ''))
        ? PolicyStatus.Inforce
        : PolicyStatus.Inactive,
      policyRoles: generatePolicyRoles(policyResponse),
    },
    policySource: _get(policyResponse, 'policySource', ''),
  };

  const risks: any[] = [];
  policy.risks = risks;

  // Process vehicles for Auto and Commercial Auto policies
  if (
    generalizedProductType === GenericProductType.Auto ||
    generalizedProductType === GenericProductType.CommercialAuto
  ) {
    _get(policyResponse, 'risk.vehicles', []).forEach((vehicle: any) => {
      const risk = {
        vehicleTypeName: _get(vehicle, 'vehicleTypeName', ''),
        // Use the policy's generalizedProductType for the risk (Auto or CommercialAuto)
        generalizedProductType: generalizedProductType,
        policySource: _get(policyResponse, 'policySource', ''),
        periodStartDate: _get(policyResponse, 'periodStartDate', ''),
        id: _get(vehicle, 'vehicleId', ''),
        policyNumber: _get(policyResponse, 'policyNumber', ''),
        partnerCode: _get(policyResponse, 'partnerCode', ''),
        address: _get(policyResponse, 'policyAddress', ''),
        description:
          _get(vehicle, 'year', '') + ' ' + _get(vehicle, 'make', '') + ' ' + _get(vehicle, 'model', '') + ' ',
        vin: _get(vehicle, 'vinSerialNumber', ''),
        iconType: 'icon-auto',
        policy: {
          policyStatus: isPolicyInForce(_get(policyResponse, 'policyTermStatus', ''))
            ? PolicyStatus.Inforce
            : PolicyStatus.Inactive,
          policyRoles: generatePolicyRoles(policyResponse),
        },
        risk: {
          year: _get(vehicle, 'year', ''),
          make: _get(vehicle, 'make', ''),
          model: _get(vehicle, 'model', ''),
        },
        year: _get(vehicle, 'year', ''),
        make: _get(vehicle, 'make', ''),
        model: _get(vehicle, 'model', ''),
        vehicleRoles: generateVehicleRoles(policyResponse),
      };
      policy.risks.push(risk);
    });
  }

  if (_get(policy, 'generalizedProductType') === GenericProductType.Home) {
    const risk = {
      generalizedProductType: GenericProductType.Home,
      policySource: _get(policyResponse, 'policySource', ''),
      periodStartDate: _get(policyResponse, 'periodStartDate', ''),
      id: uuid(),
      policyNumber: _get(policyResponse, 'policyNumber', ''),
      partnerCode: _get(policyResponse, 'partnerCode', ''),
      address: _get(
        policyResponse,
        'risk.property.propertyLocation.locationAddress',
        _get(policyResponse, 'address', '')
      ),
      description: _get(
        policyResponse,
        'risk.property.propertyLocation.locationAddress.addressLine1',
        _get(policyResponse, 'address.addressLine1', '')
      ),
      iconType: 'icon-home',
      policy: {
        policyStatus: isPolicyInForce(_get(policyResponse, 'policyTermStatus', ''))
          ? PolicyStatus.Inforce
          : PolicyStatus.Inactive,
        policyRoles: generatePolicyRoles(policyResponse),
      },
      risk: {
        addressLine1: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.addressLine1',
          _get(policyResponse, 'address.addressLine1', '')
        ),
        city: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.city',
          _get(policyResponse, 'address.city', '')
        ),
        state: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.state',
          _get(policyResponse, 'address.state', '')
        ),
        zip: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.zipCode5',
          _get(policyResponse, 'address.zipCode5', '')
        ),
      },
    };
    policy.risks.push(risk);
  }

  // Handle Workers Comp policies
  if (_get(policy, 'generalizedProductType') === GenericProductType.WorkersComp) {
    console.log('🔍 mapE1PPolicies - Creating Workers Comp risk');
    const risk = {
      generalizedProductType: GenericProductType.WorkersComp,
      policySource: _get(policyResponse, 'policySource', ''),
      periodStartDate: _get(policyResponse, 'periodStartDate', ''),
      id: uuid(),
      policyNumber: _get(policyResponse, 'policyNumber', ''),
      partnerCode: _get(policyResponse, 'partnerCode', ''),
      address: _get(policyResponse, 'address', ''),
      description: _get(policyResponse, 'contacts[0].name', 'Workers Compensation Policy'),
      iconType: 'icon-business',
      policy: {
        policyStatus: isPolicyInForce(_get(policyResponse, 'policyTermStatus', ''))
          ? PolicyStatus.Inforce
          : PolicyStatus.Inactive,
        policyRoles: generatePolicyRoles(policyResponse),
      },
      risk: {
        businessName: _get(policyResponse, 'contacts[0].name', ''),
      },
    };
    console.log('🔍 mapE1PPolicies - Workers Comp risk created:', risk);
    policy.risks.push(risk);
  }

  // Handle Commercial Property policies
  if (_get(policy, 'generalizedProductType') === GenericProductType.CommercialProperty) {
    console.log('🔍 mapE1PPolicies - Creating Commercial Property risk');
    const risk = {
      generalizedProductType: GenericProductType.CommercialProperty,
      policySource: _get(policyResponse, 'policySource', ''),
      periodStartDate: _get(policyResponse, 'periodStartDate', ''),
      id: uuid(),
      policyNumber: _get(policyResponse, 'policyNumber', ''),
      partnerCode: _get(policyResponse, 'partnerCode', ''),
      address: _get(
        policyResponse,
        'risk.property.propertyLocation.locationAddress',
        _get(policyResponse, 'address', '')
      ),
      description: _get(
        policyResponse,
        'risk.property.propertyLocation.locationAddress.addressLine1',
        _get(policyResponse, 'address.addressLine1', 'Commercial Property Policy')
      ),
      iconType: 'icon-business',
      policy: {
        policyStatus: isPolicyInForce(_get(policyResponse, 'policyTermStatus', ''))
          ? PolicyStatus.Inforce
          : PolicyStatus.Inactive,
        policyRoles: generatePolicyRoles(policyResponse),
      },
      risk: {
        addressLine1: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.addressLine1',
          _get(policyResponse, 'address.addressLine1', '')
        ),
        city: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.city',
          _get(policyResponse, 'address.city', '')
        ),
        state: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.state',
          _get(policyResponse, 'address.state', '')
        ),
        zip: _get(
          policyResponse,
          'risk.property.propertyLocation.locationAddress.zipCode5',
          _get(policyResponse, 'address.zipCode5', '')
        ),
      },
    };
    console.log('🔍 mapE1PPolicies - Commercial Property risk created:', risk);
    policy.risks.push(risk);
  }

  console.log('🔍 mapE1PPolicies - Final policy object:', policy);
  console.log('🔍 mapE1PPolicies - Policy risks count:', policy.risks.length);
  console.log('🔍 mapE1PPolicies - Policy risks:', policy.risks);

  return policy;
}

function updateRisks(policyToMap: any, generalizedProductType: GenericProductType): any {
  const mappedRisks: any[] = [];
  policyToMap.risks.forEach((risk: any, i: number) => {
    if (generalizedProductType === GenericProductType.Auto) {
      mappedRisks.push({
        id: i,
        policyNumber: _get(policyToMap, 'policyNumber', ''),
        periodStartDate: _get(policyToMap, 'periodStartDate', ''),
        description: _get(risk, 'year', '') + ' ' + _get(risk, 'make', '') + ' ' + _get(risk, 'model', '') + ' ',
        policySource: _get(policyToMap, 'policySource', ''),
        iconType: 'icon-auto',
        vehicleTypeName: _get(risk, 'vehicleTypeName', 'passenger car'),
        address: policyToMap.policyAddress,
        ...risk,
        risk: {
          year: _get(risk, 'year', ''),
          make: _get(risk, 'make', ''),
          model: _get(risk, 'model', ''),
        },
        generalizedProductType: generalizedProductType,
        policy: {
          policyStatus: mapPolicyStatus(_get(policyToMap, 'policyStatus', '')),
          policyRoles: policyToMap.policyRoles,
        },
      });
    } else {
      const address = Array.isArray(policyToMap.policyAddress)
        ? policyToMap.policyAddress[0]
        : policyToMap.policyAddress;
      mappedRisks.push({
        ...risk,
        id: i,
        policyNumber: _get(policyToMap, 'policyNumber', ''),
        periodStartDate: _get(policyToMap, 'periodStartDate', ''),
        description: _get(
          policyToMap,
          'risks.address.addressLine1',
          _get(policyToMap, 'risks[0].address[0].addressLine1', '')
        ),
        policySource: _get(policyToMap, 'policySource', ''),
        iconType: 'icon-home',
        address: address,
        risk: {
          addressLine1: _get(
            policyToMap,
            'risks.address.addressLine1',
            _get(policyToMap, 'risks[0].address[0].addressLine1', '')
          ),
          city: _get(policyToMap, 'risks.address.city', _get(policyToMap, 'risks[0].address[0].city', '')),
          state: _get(policyToMap, 'risks.address.state', _get(policyToMap, 'risks[0].address[0].state', '')),
          zip: _get(policyToMap, 'risks.address.zipCode5', _get(policyToMap, 'risks[0].address[0].zipCode5', '')),
        },
        generalizedProductType: generalizedProductType,
        policy: {
          policyStatus: mapPolicyStatus(_get(policyToMap, 'policyStatus', '')),
          policyRoles: policyToMap.policyRoles,
        },
      });
    }
  });
  return mappedRisks;
}

function generatePolicyRoles(payload: any) {
  const policyRoles: any[] = [];
  _get(payload, 'policyRoles', []).forEach((role: any) => {
    if (role.roleType !== 'BillTo') {
      const roleNameInfo = _get(payload, 'contacts', []).find((contact: any) => {
        return contact.contactReferenceKey === role.contactReferenceKey;
      });

      const updatedRole = {
        ...role,
        firstName: _get(roleNameInfo, 'customerDetail.firstName', _get(roleNameInfo, 'name', '')),
        lastName: _get(roleNameInfo, 'customerDetail.lastName', ''),
      };

      policyRoles.push(updatedRole);
    }
  });

  return policyRoles;
}

function generateVehicleRoles(payload: any) {
  const vehicleRoles: any[] = [];
  _get(payload, 'contacts', []).forEach((contact: any) => {
    if (contact.customerDetail && contact.roleType !== 'BillTo') {
      const updatedRole = {
        ...contact,
        firstName: _get(contact, 'customerDetail.firstName', ''),
        lastName: _get(contact, 'customerDetail.lastName', ''),
      };
      vehicleRoles.push(updatedRole);
    }
  });

  return vehicleRoles;
}

export function getClaimDescription(claim: any) {
  const productType = getClaimType(_get(claim, 'policyType'));

  const vehicleDetails = getVehicleDetailsText(claim);

  const displayName = _get(claim, 'displayName', '');
  const policyNumber = _get(claim, 'policyNumber', '');

  const parts = [];
  if (displayName) parts.push(displayName);
  if (policyNumber) parts.push(policyNumber);

  if (productType === GenericProductType.CommercialAuto) {
    if (vehicleDetails) parts.push(vehicleDetails);

    return parts.join('\n');
  } else if (productType === GenericProductType.CommercialProperty) {
    const lossLocation = _get(claim, 'lossLocationAddress.addressLine1', '');
    if (lossLocation) parts.push(lossLocation);
    return parts.join('\n');
  } else if (productType === GenericProductType.WorkersComp) {
    const businessName = _get(claim, 'businessName', '');
    const claimantName = _get(claim, 'claimantName', '');

    const wcParts = [];
    if (businessName) wcParts.push(businessName);
    if (policyNumber) wcParts.push(policyNumber);
    if (claimantName) wcParts.push(claimantName);

    return wcParts.join('\n');
  } else if (productType === GenericProductType.Auto) {
    return `${_get(claim, 'vehicleDetail[0].Year', '')} ${_get(claim, 'vehicleDetail[0].Make', '')} ${_get(
      claim,
      'vehicleDetail[0].Model',
      ''
    )}`.trim();
  } else {
    return productType === GenericProductType.Home
      ? _get(claim, 'lossLocationAddress.addressLine1', '')
      : 'Vehicle Not on Policy';
  }
}

// Utility function to get vehicle details text for claims list.
function getVehicleDetailsText(claim: any): string {
  const vehicles = _get(claim, 'vehicleDetail', []);
  if (vehicles.length > 1) {
    return MULTIPLE_VEHICLES_TEXT + ` (${vehicles.length})`;
  }
  return `${_get(claim, 'vehicleDetail[0].Year', '')} ${_get(claim, 'vehicleDetail[0].Make', '')} ${_get(
    claim,
    'vehicleDetail[0].Model',
    ''
  )}`.trim();
}

// Utility function to get vehicle info text for claim details subheader.
// `includeCount` appends the vehicle count to the "Multiple Vehicles" label; pass false for plain text.
export function getVehiclesInfoText(claimSummary: any, includeCount = true): string {
  const vehicles = _get(claimSummary, 'autoClaimDetails', []);
  if (vehicles.length > 1 && _get(claimSummary, 'lineOfBusiness') === GenericProductType.CommercialAuto) {
    return includeCount ? `${MULTIPLE_VEHICLES_TEXT} (${vehicles.length})` : MULTIPLE_VEHICLES_TEXT;
  }
  return `${_get(claimSummary, 'autoClaimDetails[0].year', '')} ${_get(
    claimSummary,
    'autoClaimDetails[0].make',
    ''
  )} ${_get(claimSummary, 'autoClaimDetails[0].model', '')}`.trim();
}

/**
 * Extracts the full name of the primary named insured from a policy object.
 * Handles the policy data structure where names are stored in the contacts array
 * and linked via contactReferenceKey from policyRoles.
 *
 * @param policy - The policy object containing policyRoles and contacts
 * @returns The full name (firstName + lastName) or empty string if not found
 */
export function getPrimaryNamedInsuredFullName(policy: any): string {
  if (!policy) {
    return '';
  }

  const policyRoles = _get(policy, 'policyRoles', []);
  const primaryNamedInsured = policyRoles.find((role: any) => _get(role, 'roleType') === 'PrimaryNamedInsured');

  if (!primaryNamedInsured) {
    return '';
  }

  const contactReferenceKey = _get(primaryNamedInsured, 'contactReferenceKey', '');
  if (!contactReferenceKey) {
    return '';
  }

  const contacts = _get(policy, 'contacts', []);
  const matchingContact = contacts.find((contact: any) => _get(contact, 'contactReferenceKey') === contactReferenceKey);

  if (!matchingContact) {
    return '';
  }

  const firstName = _get(matchingContact, 'customerDetail.firstName', '').trim();
  const lastName = _get(matchingContact, 'customerDetail.lastName', '').trim();

  // For Workers Comp, the name might be in the firstName field only (business name)
  if (firstName && !lastName) {
    return firstName;
  }
  // For individual names with both first and last
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  // Fallback to firstName if available
  if (firstName) {
    return firstName;
  }

  return '';
}
